import { getItem } from '../data/items.js';
import { planGrant } from './inventory.js';
/**
 * Trading between two players (ADR 0034), as pure rules: what may be
 * offered, whether the two sides are close enough, and whether what one
 * receives still fits in one's bag once what one gives has left it. The
 * room keeps the trades themselves (`TradeDesk`); `TradeService` applies the
 * exchange in one transaction and checks all of this again under lock.
 */
/** Stacks one side may put on the table at once. */
export const TRADE_MAX_ITEMS = 8;
/** Both players stand within this many tiles of each other, diagonals included, throughout. */
export const TRADE_REACH = 4;
/** A request not answered in this time lapses. */
export const TRADE_REQUEST_TTL_MS = 30_000;
/** More gold than any purse holds (`ADMIN_MAX_GOLD`): a bound for the schema, not a rule. */
export const TRADE_MAX_GOLD = 10_000_000;
/**
 * A piece bought in the shop waits this long after delivery before it may be
 * traded or auctioned (ADR 0044): longer than a refund usually takes, so a
 * purchase cannot be turned into gold and then refunded.
 */
export const PURCHASE_TRADE_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
/** When a purchase delivered at `deliveredAt` (ms) becomes tradable. */
export function purchaseTradableAfter(deliveredAt) {
    return new Date(deliveredAt + PURCHASE_TRADE_COOLDOWN_MS);
}
/** Why an instance may not change hands now, or null when it may. */
export function tradeBlock(instance, now = Date.now()) {
    if (instance.bound)
        return 'bound';
    if (instance.tradableAfter !== undefined && Date.parse(instance.tradableAfter) > now) {
        return 'cooldown';
    }
    return null;
}
/**
 * A bound piece (ADR 0013, from +4) never changes hands, a shop purchase not
 * before its cooldown (ADR 0044); everything else in the bag may.
 */
export function isTradable(instance, now = Date.now()) {
    return tradeBlock(instance, now) === null;
}
export function withinTradeReach(a, b) {
    return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y)) <= TRADE_REACH;
}
/**
 * Whether one side can give what it offers: every stack is in its bag, in
 * that quantity, unbound and past any purchase cooldown at `now`, and the
 * gold is in its purse. Null when it can.
 */
export function offerProblem(bag, gold, offer, now = Date.now()) {
    if (offer.items.length > TRADE_MAX_ITEMS)
        return 'invalid';
    if (new Set(offer.items.map((stack) => stack.itemId)).size !== offer.items.length) {
        return 'invalid';
    }
    for (const stack of offer.items) {
        const entry = bag.find((candidate) => candidate.item.id === stack.itemId);
        if (entry === undefined)
            return 'unknown-item';
        if (!Number.isInteger(stack.quantity) || stack.quantity < 1)
            return 'quantity';
        if (stack.quantity > entry.item.quantity)
            return 'quantity';
        const blocked = tradeBlock(entry.item, now);
        if (blocked !== null)
            return blocked;
    }
    if (!Number.isInteger(offer.gold) || offer.gold < 0)
        return 'invalid';
    if (offer.gold > gold)
        return 'missing-gold';
    return null;
}
/**
 * Whether everything in `incoming` fits in `bag` once `outgoing` has left
 * it. A piece of equipment keeps its own row (its smith's level with it), so
 * it never merges with a stack of the same template: each one needs a slot
 * of its own, which `planGrant` gives it since equipment does not stack.
 */
export function receiveFits(bag, outgoing, incoming, bagSize) {
    let remaining = bag.map((entry) => ({ slot: entry.slot, item: { ...entry.item } }));
    for (const stack of outgoing) {
        const entry = remaining.find((candidate) => candidate.item.id === stack.itemId);
        if (entry === undefined)
            continue;
        if (stack.quantity >= entry.item.quantity) {
            remaining = remaining.filter((candidate) => candidate !== entry);
        }
        else {
            entry.item.quantity -= stack.quantity;
        }
    }
    let serial = 0;
    for (const stack of incoming) {
        const template = getItem(stack.templateId);
        if (template === undefined)
            return false;
        // Equipment never stacks (`maxStack` 1), so an upgraded piece always
        // takes a fresh slot of its own and its smith's level travels with it.
        const plan = planGrant(remaining, template, stack.quantity, bagSize);
        if (plan === null)
            return false;
        for (const placement of plan) {
            const existing = placement.itemId === null
                ? undefined
                : remaining.find((entry) => entry.item.id === placement.itemId);
            if (existing !== undefined) {
                existing.item.quantity += placement.quantity;
            }
            else {
                serial += 1;
                remaining.push({
                    slot: placement.slot,
                    item: {
                        id: `incoming-${String(serial)}`,
                        templateId: template.id,
                        quantity: placement.quantity,
                        upgradeLevel: stack.upgradeLevel,
                        bound: false,
                    },
                });
            }
        }
    }
    return true;
}
//# sourceMappingURL=trade.js.map